import * as TagManager from 'react-gtm-module';

import { AnalyticsEventParams, AnalyticsService, ConsentStatus } from '../types/analytics';
import { ANALYTICS_CONFIG } from './analytics-config';
import { analyticsMonitor } from './analyticsMonitor';
import { analyticsOptimizer } from './analyticsOptimizer';
import { consentManager } from './consentManager';

/**
 * Retry logic con exponential backoff para servicios de analytics
 */

/**
 * Valida que la configuración de analytics esté correctamente inyectada
 */
const validateAnalyticsConfig = (): boolean => {
  const config = ANALYTICS_CONFIG;

  // Verificar que las variables no contengan placeholders sin reemplazar
  const hasPlaceholders = Object.values(config).some(
    value => typeof value === 'string' && value.includes('${')
  );

  if (hasPlaceholders) {
    if (!import.meta.env.PROD) {
      console.error(
        '❌ [Analytics] Variables ENV no inyectadas correctamente.\n' +
          '   Verifica que vite-html-env-plugin esté configurado y que el build se ejecutó correctamente.'
      );
    }
    return false;
  }

  // Verificar variables críticas
  if (!config.GTM_ID || !config.FB_PIXEL_ID || !config.CLARITY_ID) {
    if (!import.meta.env.PROD) {
      console.warn(
        '⚠️  [Analytics] Algunas variables críticas están vacías.\n' +
          `   GTM_ID: ${config.GTM_ID ? '✓' : '✗'}\n` +
          `   FB_PIXEL_ID: ${config.FB_PIXEL_ID ? '✓' : '✗'}\n` +
          `   CLARITY_ID: ${config.CLARITY_ID ? '✓' : '✗'}`
      );
    }
  }

  return true;
};

/**
 * Inyecta scripts de analytics dinámicamente después del consentimiento
 */
const injectAnalyticsScripts = async (): Promise<void> => {
  if (!validateAnalyticsConfig()) {
    return;
  }

  const scriptsToInject: Array<{ id: string; src?: string; innerHTML?: string; async?: boolean }> =
    [];

  // Google Tag Manager
  if (ANALYTICS_CONFIG.GTM_ID && consentManager.hasAnalyticsConsent()) {
    scriptsToInject.push({
      id: 'gtm-script',
      src: `https://www.googletagmanager.com/gtm.js?id=${ANALYTICS_CONFIG.GTM_ID}`,
      async: true,
    });
  }

  // Microsoft Clarity
  if (ANALYTICS_CONFIG.CLARITY_ID && consentManager.hasAnalyticsConsent()) {
    scriptsToInject.push({
      id: 'clarity-script',
      innerHTML: `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${ANALYTICS_CONFIG.CLARITY_ID}");
      `,
    });
  }

  // Facebook Pixel
  if (ANALYTICS_CONFIG.FB_PIXEL_ID && consentManager.hasAdvertisingConsent()) {
    scriptsToInject.push({
      id: 'fb-pixel-script',
      innerHTML: `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${ANALYTICS_CONFIG.FB_PIXEL_ID}');
        fbq('track', 'PageView');
      `,
      async: true,
    });
  }

  // LinkedIn Insight Tag
  if (ANALYTICS_CONFIG.LINKEDIN_ID && consentManager.hasAdvertisingConsent()) {
    scriptsToInject.push({
      id: 'linkedin-script',
      innerHTML: `
        _linkedin_partner_id = "${ANALYTICS_CONFIG.LINKEDIN_ID}";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);
      `,
    });

    scriptsToInject.push({
      id: 'linkedin-insight-script',
      innerHTML: `
        (function(l) {
          if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a, b])};
          window.lintrk.q=[]}
          var s = document.getElementsByTagName("script")[0];
          var b = document.createElement("script");
          b.type = "text/javascript";b.async = true;
          b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
          s.parentNode.insertBefore(b, s);
        })(window.lintrk);
      `,
      async: true,
    });
  }

  // Hotjar
  if (
    ANALYTICS_CONFIG.HOTJAR_ID &&
    ANALYTICS_CONFIG.HOTJAR_VERSION &&
    consentManager.hasAnalyticsConsent()
  ) {
    scriptsToInject.push({
      id: 'hotjar-script',
      innerHTML: `
        (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:${ANALYTICS_CONFIG.HOTJAR_ID},hjsv:${ANALYTICS_CONFIG.HOTJAR_VERSION}};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `,
      async: true,
    });
    window.analyticsDebug?.log('Hotjar', 'Script preparado para inyección');
  }

  // LogRocket
  if (ANALYTICS_CONFIG.LOGROCKET_ID && consentManager.hasAnalyticsConsent()) {
    scriptsToInject.push({
      id: 'logrocket-script',
      innerHTML: `
        window._LRLogger = window._LRLogger || [];
        window._LRLogger.push(['init', '${ANALYTICS_CONFIG.LOGROCKET_ID}']);
        (function(){
          var script = document.createElement('script');
          script.async = true;
          script.src = 'https://cdn.lr-ingest.io/LogRocket.min.js';
          script.crossOrigin = 'anonymous';
          document.head.appendChild(script);
          script.onload = function() {
            if (window.LogRocket) {
              window.LogRocket.init('${ANALYTICS_CONFIG.LOGROCKET_ID}');
              console.log('✅ LogRocket initialized');
            }
          };
        })();
      `,
      async: true,
    });
    window.analyticsDebug?.log('LogRocket', 'Script preparado para inyección');
  }

  // Sentry
  if (ANALYTICS_CONFIG.SENTRY_DSN && consentManager.hasFunctionalConsent()) {
    scriptsToInject.push({
      id: 'sentry-script',
      innerHTML: `
        (function() {
          var script = document.createElement('script');
          script.src = 'https://browser.sentry-cdn.com/7.91.0/bundle.min.js';
          script.crossOrigin = 'anonymous';
          script.onload = function() {
            if (window.Sentry) {
              window.Sentry.init({
                dsn: '${ANALYTICS_CONFIG.SENTRY_DSN}',
                integrations: [
                  new window.Sentry.BrowserTracing(),
                  new window.Sentry.Replay()
                ],
                tracesSampleRate: 1.0,
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0,
                environment: '${process.env.NODE_ENV || 'production'}',
                beforeSend(event) {
                  // Filtrar errores de extensiones del navegador
                  if (event.exception) {
                    const frames = event.exception.values?.[0]?.stacktrace?.frames;
                    if (frames && frames.some(frame => 
                      frame.filename?.includes('chrome-extension://') || 
                      frame.filename?.includes('moz-extension://')
                    )) {
                      return null;
                    }
                  }
                  return event;
                }
              });
              console.log('✅ Sentry initialized');
            }
          };
          document.head.appendChild(script);
        })();
      `,
      async: true,
    });
    window.analyticsDebug?.log('Sentry', 'Script preparado para inyección');
  }

  // Inyectar scripts en el DOM
  scriptsToInject.forEach(({ id, src, innerHTML, async }) => {
    // Evitar duplicados
    if (document.getElementById(id)) {
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    if (src) {
      script.src = src;
    }
    if (innerHTML) {
      script.innerHTML = innerHTML;
    }
    if (async) {
      script.async = true;
    }

    document.head.appendChild(script);
    window.analyticsDebug?.log('Script Injection', `Inyectado: ${id}`);
  });
};

/**
 * Monitor Google Search Console verification
 */
const monitorGSCVerification = () => {
  try {
    const metaTag = document.querySelector('meta[name="google-site-verification"]');
    if (metaTag) {
      window.analyticsDebug?.log('GSC', `Meta Tag presente: ${metaTag.getAttribute('content')}`);
    }
  } catch (error) {
    console.error('Error verificando GSC:', error);
  }
};

/**
 * Backup verification if meta tag is missing
 */
const backupGSCVerification = () => {
  if (!document.querySelector('meta[name="google-site-verification"]')) {
    const meta = document.createElement('meta');
    meta.name = 'google-site-verification';
    meta.content = 'y-5CxXgDxP_LV-Rq8yj7tKPjLTugjNSkn8zinHUQXeg';
    document.head.appendChild(meta);
    window.analyticsDebug?.log('GSC', 'Meta Tag respaldo agregado');
  }
};

/**
 * Initialize analytics services with proper error handling and debugging
 * Modified to prevent blocking UI rendering when analytics services fail
 * This function now waits for the window.load event to ensure all external
 * scripts are loaded before attempting initialization, preventing race conditions.
 */
export const initializeAnalytics = async (): Promise<void> => {
  // Evitar múltiples inicializaciones
  if ((window as any).__analyticsInitialized) {
    return Promise.resolve();
  }

  return new Promise((resolve, _reject) => {
    const runInitialization = async () => {
      try {
        // Marcar como inicializado al principio para evitar múltiples intentos
        (window as any).__analyticsInitialized = true;

        // Ensure consent manager is initialized first
        await new Promise<void>(consentResolve => {
          try {
            const consentStatus = consentManager.getConsentStatus();
            // Initialize services with consent status
            if (window.dataLayer) {
              window.dataLayer.push(['event', 'consent_status', { consent: consentStatus }]);
            }
            consentResolve();
          } catch (consentError) {
            console.error('Error initializing consent manager:', consentError);
            // Continue with default consent values
            consentResolve();
          }
        });

        // Inyectar scripts de analytics solo después del consentimiento
        await injectAnalyticsScripts();

        // Monitor GSC verification - wrapped in try/catch to prevent failures
        try {
          monitorGSCVerification();
          backupGSCVerification();
        } catch (gscError) {
          console.error('Error durante la verificación de Google Search Console:', gscError);
        }

        // Initialize services with proper error handling
        const services: AnalyticsService[] = [
          {
            name: 'Google Tag Manager',
            init: async () => {
              if (ANALYTICS_CONFIG.GTM_ID && consentManager.hasAnalyticsConsent()) {
                try {
                  const tagManagerArgs: TagManager.TagManagerArgs = {
                    gtmId: ANALYTICS_CONFIG.GTM_ID,
                    dataLayer: {
                      'gtm.start': new Date().getTime(),
                      event: 'gtm.js',
                      consentGranted: true,
                    },
                  };
                  // Use Promise with timeout to prevent hanging
                  await Promise.race([
                    TagManager.default.initialize(tagManagerArgs),
                    new Promise((_, reject) =>
                      setTimeout(() => reject(new Error('GTM initialization timeout')), 2000)
                    ),
                  ]);
                  window.analyticsDebug?.log('GTM', 'Initialized');
                } catch (error) {
                  analyticsOptimizer.trackError('GTM');
                  console.error('Error al inicializar Google Tag Manager:', error);
                  // Don't rethrow error, just handle it silently
                }
              }
            },
            trackEvent: (eventName: string, params?: AnalyticsEventParams) => {
              if (window.dataLayer && consentManager.hasAnalyticsConsent()) {
                try {
                  window.dataLayer.push(['event', eventName, params]);
                  window.analyticsDebug?.log('GTM', { event: eventName, params });
                } catch (error) {
                  analyticsOptimizer.trackError('GTM');
                  console.error(`Error al rastrear el evento GTM '${eventName}':`, error);
                  // Silent error handling
                }
              }
            },
          },
          {
            name: 'Microsoft Clarity',
            init: async () => {
              if (ANALYTICS_CONFIG.CLARITY_ID && typeof window.clarity === 'function') {
                try {
                  if (consentManager.hasAnalyticsConsent()) {
                    window.clarity('consent');
                    window.analyticsDebug?.log('Clarity', 'Initialized with consent');
                  }
                } catch (error) {
                  analyticsOptimizer.trackError('CLARITY');
                  throw error;
                }
              }
            },
          },
          {
            name: 'Facebook Pixel',
            init: async () => {
              if (ANALYTICS_CONFIG.FB_PIXEL_ID && typeof window.fbq === 'function') {
                try {
                  if (consentManager.hasAdvertisingConsent()) {
                    window.fbq('init', ANALYTICS_CONFIG.FB_PIXEL_ID);
                    window.fbq('track', 'PageView');
                    window.analyticsDebug?.log('Facebook Pixel', 'Initialized with consent');
                  }
                } catch (error) {
                  analyticsOptimizer.trackError('FB_PIXEL');
                  throw error;
                }
              }
            },
            trackEvent: (eventName: string, params?: AnalyticsEventParams) => {
              if (typeof window.fbq === 'function' && consentManager.hasAdvertisingConsent()) {
                try {
                  window.fbq('track', eventName, params);
                  window.analyticsDebug?.log('Facebook Pixel', { event: eventName, params });
                } catch (error) {
                  analyticsOptimizer.trackError('FB_PIXEL');
                  console.error('Error tracking FB event:', error);
                }
              }
            },
          },
          {
            name: 'LinkedIn Insight',
            init: async () => {
              if (ANALYTICS_CONFIG.LINKEDIN_ID && typeof window.lintrk === 'function') {
                try {
                  if (consentManager.hasAdvertisingConsent()) {
                    window.lintrk('track', { conversion_id: ANALYTICS_CONFIG.LINKEDIN_ID });
                    window.analyticsDebug?.log('LinkedIn', 'Initialized with consent');
                  }
                } catch (error) {
                  analyticsOptimizer.trackError('LINKEDIN');
                  throw error;
                }
              }
            },
            trackEvent: (eventName: string, params?: AnalyticsEventParams) => {
              if (typeof window.lintrk === 'function' && consentManager.hasAdvertisingConsent()) {
                try {
                  window.lintrk('track', {
                    conversion_id: ANALYTICS_CONFIG.LINKEDIN_ID,
                    ...params,
                  });
                  window.analyticsDebug?.log('LinkedIn', { event: eventName, params });
                } catch (error) {
                  analyticsOptimizer.trackError('LINKEDIN');
                  console.error('Error tracking LinkedIn event:', error);
                }
              }
            },
          },
        ];

        // Optimizar orden de carga
        const optimizedServices = analyticsOptimizer.optimizeLoadOrder(services);

        // Initialize services with timeout
        const initPromise = initializeBaseTracking(optimizedServices);
        await Promise.race([
          initPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Analytics initialization timeout')), 5000)
          ),
        ]).catch(error => {
          console.error('Error or timeout in analytics initialization:', error);
        });

        // Iniciar monitoreo
        analyticsMonitor.checkServices();

        // Listen for consent changes
        consentManager.onConsentChange(status => {
          if (status.analytics || status.advertising) {
            reinitializeServices(optimizedServices, status).catch(error => {
              console.error('Error reinitializing services:', error);
            });
          }
        });
      } catch (error) {
        console.error('Error initializing analytics:', error);
        analyticsMonitor.checkServices(); // Verificar estado después de error
      } finally {
        resolve();
      }
    };

    // Ejecutar la inicialización solo cuando la página esté completamente cargada
    if (document.readyState === 'complete') {
      runInitialization();
    } else {
      window.addEventListener('load', runInitialization, { once: true });
    }
  });
};

const initializeBaseTracking = async (services: AnalyticsService[]): Promise<void> => {
  for (const service of services) {
    try {
      await service.init();
    } catch (error) {
      console.error(`Error initializing ${service.name}:`, error);
      analyticsOptimizer.trackError(service.name);
    }
  }
};

const reinitializeServices = async (
  services: AnalyticsService[],
  status: { analytics: boolean; advertising: boolean; functional: boolean }
): Promise<void> => {
  try {
    if (status.analytics) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted' as ConsentStatus,
        });
      }
      if (typeof window.clarity === 'function') {
        window.clarity('consent');
      }
    }

    if (status.advertising) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ad_storage: 'granted' as ConsentStatus,
        });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'grant');
      }
      if (typeof window.lintrk === 'function') {
        window.lintrk('track', { conversion_id: ANALYTICS_CONFIG.LINKEDIN_ID });
      }
    }

    // Track consent granted event
    services.forEach(service => {
      if (service.trackEvent) {
        service.trackEvent('consent_update', { status });
      }
    });

    // Verificar estado después de reinicialización
    analyticsMonitor.checkServices();
  } catch (error) {
    console.error('Error reinitializing services:', error);
    analyticsOptimizer.trackError('REINITIALIZATION');
  }
};

export default initializeAnalytics;
